import { Card } from "@/components/ui/card";
import { FormHeader } from "@/components/FormHeader";
import { FormCover } from "@/components/FormCover";
import { QuestionRenderer } from "@/components/form-questions/QuestionRenderer";
import type { FormQuestion, FormTheme } from "@kushya/shared";
import type { ValidationError as SubmissionValidationError } from "@/api/responses";
import { FormProgress } from "./runtime/FormProgress";
import { SubmissionAlert } from "./runtime/SubmissionAlert";
import { ValidationSummary } from "./runtime/ValidationSummary";
import { QuestionNavigation } from "./runtime/QuestionNavigation";
import { KeyboardHint } from "./runtime/KeyboardHint";

export interface RuntimeScreenProps {
  projectId: string;
  formId: string;
  version: number;
  alignment: "left" | "center" | "right";
  logoUrl?: string;
  formName?: string;
  theme?: FormTheme;
  showProgress: boolean;
  progressMode: "pages" | "questions" | "percentage";
  progressPercentage: number;
  currentPage?: number;
  totalPages?: number;
  currentStep: number;
  totalQuestions: number;
  submissionError: string | null;
  validationErrors: SubmissionValidationError[];
  onDismissSubmissionError: () => void;
  currentQuestion?: FormQuestion;
  currentPageQuestions?: FormQuestion[];
  questions: FormQuestion[];
  answers: Record<string, unknown>;
  sessionToken: string | null;
  ensureSessionToken: () => Promise<string | null>;
  onAnswerChange: (questionRef: string, value: unknown) => void;
  onAnswerBlur: (questionRef: string) => void;
  getQuestionError: (questionRef: string) => string | undefined;
  onBack: () => void;
  onNext: () => void;
  canProceed: boolean;
  isSubmitting: boolean;
  backDisabled: boolean;
  isLastStep: boolean;
  isSharedMode?: boolean;
}

export function RuntimeScreen({
  projectId,
  formId,
  version,
  alignment,
  logoUrl,
  formName,
  theme,
  showProgress,
  progressMode,
  progressPercentage,
  currentPage,
  totalPages,
  currentStep,
  totalQuestions,
  submissionError,
  validationErrors,
  onDismissSubmissionError,
  currentQuestion,
  answers,
  currentPageQuestions,
  questions,
  onAnswerChange,
  onAnswerBlur,
  getQuestionError,
  sessionToken,
  ensureSessionToken,
  onBack,
  onNext,
  canProceed,
  isSubmitting,
  backDisabled,
  isLastStep,
  isSharedMode = false,
}: RuntimeScreenProps) {
  return (
    <>
      <div className="form-shell">
        <div className="mx-auto max-w-2xl space-y-6">
          <FormHeader
            logoUrl={logoUrl}
            formName={formName}
            alignment={alignment}
          />

          {showProgress ? (
            <FormProgress
              mode={progressMode}
              currentStep={currentStep}
              totalQuestions={totalQuestions}
              currentPage={currentPage}
              totalPages={totalPages}
              progressPercentage={progressPercentage}
            />
          ) : null}

          <SubmissionAlert
            message={submissionError}
            onDismiss={onDismissSubmissionError}
            hasValidationErrors={validationErrors.length > 0}
          />

          <Card className="form-paper p-6 md:p-8">
            <FormCover theme={theme} alignment={alignment} />
            {currentPageQuestions && currentPageQuestions.length > 0 ? (
              <div key={`step-${currentStep}`} className="form-step space-y-8">
                <ValidationSummary
                  validationErrors={validationErrors}
                  questions={questions}
                />

                {currentPageQuestions.map((question) => (
                  <QuestionRenderer
                    key={question.id}
                    question={question}
                    value={answers[question.ref]}
                    onChange={(value) => onAnswerChange(question.ref, value)}
                    onBlur={() => onAnswerBlur(question.ref)}
                    error={getQuestionError(question.ref)}
                    sessionToken={sessionToken}
                    ensureSessionToken={ensureSessionToken}
                    projectId={projectId}
                    formId={formId}
                    version={version}
                    mode="runtime"
                  />
                ))}

                <QuestionNavigation
                  onBack={onBack}
                  onNext={onNext}
                  canProceed={canProceed}
                  isSubmitting={isSubmitting}
                  currentStep={currentStep}
                  totalQuestions={totalQuestions}
                  backDisabled={backDisabled}
                  isLastStep={isLastStep}
                  isSharedMode={isSharedMode}
                />
              </div>
            ) : currentQuestion ? (
              <div key={`step-${currentStep}`} className="form-step space-y-8">
                <ValidationSummary
                  validationErrors={validationErrors}
                  questions={questions}
                />

                <QuestionRenderer
                  question={currentQuestion}
                  value={answers[currentQuestion.ref]}
                  onChange={(value) =>
                    onAnswerChange(currentQuestion.ref, value)
                  }
                  onBlur={() => onAnswerBlur(currentQuestion.ref)}
                  error={getQuestionError(currentQuestion.ref)}
                  sessionToken={sessionToken}
                  ensureSessionToken={ensureSessionToken}
                  projectId={projectId}
                  formId={formId}
                  version={version}
                  mode="runtime"
                />

                <QuestionNavigation
                  onBack={onBack}
                  onNext={onNext}
                  canProceed={canProceed}
                  isSubmitting={isSubmitting}
                  currentStep={currentStep}
                  totalQuestions={totalQuestions}
                  backDisabled={backDisabled}
                  isLastStep={isLastStep}
                  isSharedMode={isSharedMode}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                This form has no questions to display.
              </p>
            )}
          </Card>

          <KeyboardHint />

          <div className="text-[11px] text-muted-foreground/50">
            Powered by{" "}
            <a
              href="https://usemojo.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-muted-foreground/70"
            >
              usemojo.ai
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
